import { DestroyRef, inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { BreakpointObserver } from '@angular/cdk/layout';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/** Où l'on se souvient que l'utilisateur a épinglé le menu déplié. */
const SIDENAV_PINNED_STORAGE_KEY = 'proporio.sidenav.pinned';

function readPinnedPreference(): boolean {
  try {
    return localStorage.getItem(SIDENAV_PINNED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

@Injectable({
  providedIn: 'root'
})
export class VexLayoutService {
  private _quickpanelOpenSubject = new BehaviorSubject<boolean>(false);
  quickpanelOpen$ = this._quickpanelOpenSubject.asObservable();

  private _sidenavOpenSubject = new BehaviorSubject<boolean>(false);
  sidenavOpen$ = this._sidenavOpenSubject.asObservable();

  private _sidenavCollapsedSubject = new BehaviorSubject<boolean>(false);
  sidenavCollapsed$ = this._sidenavCollapsedSubject.asObservable();

  private _sidenavCollapsedOpenSubject = new BehaviorSubject<boolean>(false);
  sidenavCollapsedOpen$ = this._sidenavCollapsedOpenSubject.asObservable();

  private _sidenavPinnedSubject = new BehaviorSubject<boolean>(
    readPinnedPreference()
  );
  /** Épinglé = menu déplié classique, contenu décalé. Pas de rail, pas de survol. */
  sidenavPinned$ = this._sidenavPinnedSubject.asObservable();

  private _contextualSidenavOpen = new BehaviorSubject<boolean>(false);
  contextualSidenavOpen$ = this._contextualSidenavOpen.asObservable();

  protected destroyRef: DestroyRef = inject(DestroyRef);
  private _configPanelOpenSubject = new BehaviorSubject<boolean>(false);

  private _searchOpen = new BehaviorSubject<boolean>(false);
  searchOpen$ = this._searchOpen.asObservable();

  isDesktop$ = this.breakpointObserver
    .observe(`(min-width: 1280px)`)
    .pipe(map((state) => state.matches));
  ltLg$ = this.breakpointObserver
    .observe(`(max-width: 1279px)`)
    .pipe(map((state) => state.matches));
  gtMd$ = this.breakpointObserver
    .observe(`(min-width: 960px)`)
    .pipe(map((state) => state.matches));
  ltMd$ = this.breakpointObserver
    .observe(`(max-width: 959px)`)
    .pipe(map((state) => state.matches));
  gtSm$ = this.breakpointObserver
    .observe(`(min-width: 600px)`)
    .pipe(map((state) => state.matches));
  isMobile$ = this.breakpointObserver
    .observe(`(max-width: 599px)`)
    .pipe(map((state) => state.matches));
  /**
   * Machine à souris. Sans survol (tablette en paysage, écran tactile), un rail
   * d'icônes serait une impasse : on y laisse le menu déplié.
   */
  canHover$ = this.breakpointObserver
    .observe(`(hover: hover) and (pointer: fine)`)
    .pipe(map((state) => state.matches));

  /**
   * Vrai seulement pendant l'ouverture au survol d'un rail replié — jamais en
   * mode `over`, jamais quand le menu est épinglé. C'est ce qui décide de
   * l'ombre portée : elle ne se justifie que lorsque le panneau se superpose
   * vraiment au contenu.
   */
  sidenavRailHoverOpen$ = combineLatest([
    this.sidenavCollapsed$,
    this.sidenavCollapsedOpen$
  ]).pipe(
    map(([collapsed, open]) => collapsed && open),
    distinctUntilChanged()
  );

  /**
   * Là où un rail d'icônes a un sens : assez large pour le mode `side`, et une
   * souris pour le déplier. C'est la seule condition qui rend l'épingle utile —
   * et la seule qui doit décider de son affichage. La gater sur l'état épinglé
   * la faisait disparaître au rechargement d'une session épinglée, laissant
   * l'utilisateur sans moyen de dépingler (constaté sur dev le 2026-09-23).
   */
  sidenavRailCapable$ = combineLatest([this.gtMd$, this.canHover$]).pipe(
    map(([gtMd, canHover]) => gtMd && canHover),
    distinctUntilChanged()
  );

  isLtLg = () => this.breakpointObserver.isMatched(`(max-width: 1279px)`);
  /** Sync match for overlay-drawer screens (sidenav mode `over`). */
  isLtMd = () => this.breakpointObserver.isMatched(`(max-width: 959px)`);

  isMobile = () => this.breakpointObserver.isMatched(`(max-width: 599px)`);
  configPanelOpen$ = this._configPanelOpenSubject.asObservable();

  constructor(private readonly breakpointObserver: BreakpointObserver) {
    /**
     * Le rail d'icônes est l'état permanent du bureau. Une seule règle, trois
     * conditions, toutes nécessaires :
     * - ≥960px, donc le tiroir est en mode `side` (sous 960px c'est un tiroir
     *   pleine largeur avec backdrop : un rail d'icônes y serait illisible) ;
     * - la machine sait survoler, sinon le rail ne se déplierait jamais ;
     * - le menu n'est pas épinglé, l'épingle étant justement la demande
     *   explicite de retrouver le menu déplié avec le contenu décalé.
     *
     * Tant que ces trois conditions tiennent, `sidenavCollapsed$` reste à
     * `true` pendant le survol — c'est lui qui fige la marge du contenu. Voir
     * `sidenavCollapsedOpen$` pour l'état de survol lui-même.
     */
    combineLatest([this.gtMd$, this.canHover$, this.sidenavPinned$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([isGtMd, canHover, pinned]) => {
        if (isGtMd && canHover && !pinned) {
          this.collapseSidenav();
        } else {
          this.expandSidenav();
        }
      });
  }

  openQuickpanel() {
    this._quickpanelOpenSubject.next(true);
  }

  closeQuickpanel() {
    this._quickpanelOpenSubject.next(false);
  }

  openSidenav() {
    this._sidenavOpenSubject.next(true);
  }

  closeSidenav() {
    this._sidenavOpenSubject.next(false);
  }

  collapseSidenav() {
    this._sidenavCollapsedSubject.next(true);
  }

  expandSidenav() {
    this._sidenavCollapsedSubject.next(false);
  }

  collapseOpenSidenav() {
    this._sidenavCollapsedOpenSubject.next(true);
  }

  collapseCloseSidenav() {
    this._sidenavCollapsedOpenSubject.next(false);
  }

  toggleSidenavPin() {
    const pinned = !this._sidenavPinnedSubject.value;
    this._sidenavPinnedSubject.next(pinned);

    // Le survol en cours n'a plus de sens une fois le menu épinglé : il
    // laisserait `.open` posé sur un panneau qui n'est plus un rail.
    this._sidenavCollapsedOpenSubject.next(false);

    try {
      localStorage.setItem(SIDENAV_PINNED_STORAGE_KEY, pinned ? '1' : '0');
    } catch {
      // Navigation privée, stockage refusé : l'épingle vaut pour la session.
    }
  }

  openContextualSidenav(): void {
    this._contextualSidenavOpen.next(true);
  }

  closeContextualSidenav(): void {
    this._contextualSidenavOpen.next(false);
  }

  toggleContextualSidenav(): void {
    this._contextualSidenavOpen.next(!this._contextualSidenavOpen.value);
  }

  openConfigpanel() {
    this._configPanelOpenSubject.next(true);
  }

  closeConfigpanel() {
    this._configPanelOpenSubject.next(false);
  }

  openSearch() {
    this._searchOpen.next(true);
  }

  closeSearch() {
    this._searchOpen.next(false);
  }
}
